import React, { useState } from "react"
import i18n from "i18n"
import PropTypes from "prop-types"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import IconButton from "@mui/material/IconButton"
import Button from "@mui/material/Button"
import Box from "@mui/material/Box"
import CircularProgress from "@mui/material/CircularProgress"
import Tabs from "@mui/material/Tabs"
import Tab from "@mui/material/Tab"
import Typography from "@mui/material/Typography"
import Chip from "@mui/material/Chip"
import Avatar from "@mui/material/Avatar"
import CloseIcon from "@mui/icons-material/Close"
import AddIcon from "@mui/icons-material/Add"
import EditIcon from "@mui/icons-material/Edit"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import { useSnackbar } from "notistack"
import { useAuthContext } from "context/auth/authContext"
import EditBrandGeneralTab from "./EditBrandGeneralTab"
import EditBrandPdfTab from "./EditBrandPdfTab"
import {
  useBrands,
  useCreateBrand,
  useUpdateBrand,
  useDeleteBrand,
  useSetDefaultBrand,
} from "./hooks/useBrands"

// Mirrors budgetPdf3 set_styles: color_primary is the chapter text, secondary its background.
const DEFAULT_TEXT_COLOR = "#000000"
const DEFAULT_BACKGROUND_COLOR = "#FFFFFF"

const BrandCard = ({ brand, onEdit, onDelete }) => (
  <Box
    sx={{
      border: "1px solid #e0e0e0",
      borderRadius: "8px",
      p: 2,
      mb: 2,
      backgroundColor: "#fff",
      display: "flex",
      alignItems: "center",
      gap: 2,
    }}
  >
    <Avatar
      sx={{
        width: 52,
        height: 52,
        backgroundColor: brand.colorSecondary || DEFAULT_BACKGROUND_COLOR,
        color: brand.colorPrimary || DEFAULT_TEXT_COLOR,
        fontSize: "16px",
        fontWeight: 700,
        flexShrink: 0,
        border: "1px solid #e0e0e0",
      }}
    >
      {brand.initials}
    </Avatar>

    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography
          sx={{ fontWeight: 600, fontSize: "15px", color: "#414141" }}
        >
          {brand.name}
        </Typography>
        {brand.isDefault && (
          <Chip
            label={i18n.t("trademarkSettings.defaultBadge")}
            size="small"
            sx={{
              backgroundColor: "#4A90D9",
              color: "#fff",
              fontWeight: 600,
              fontSize: "11px",
              height: 20,
            }}
          />
        )}
      </Box>
    </Box>

    <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
      <IconButton
        size="small"
        onClick={() => onEdit(brand)}
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: "6px",
          color: "#414141",
          "&:hover": { backgroundColor: "#f5f5f5" },
        }}
      >
        <EditIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => onDelete(brand)}
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: "6px",
          color: "#e53935",
          "&:hover": { backgroundColor: "#fdecea" },
        }}
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Box>
  </Box>
)

BrandCard.propTypes = {
  brand: PropTypes.shape({
    initials: PropTypes.string,
    name: PropTypes.string,
    isDefault: PropTypes.bool,
    colorPrimary: PropTypes.string,
    colorSecondary: PropTypes.string,
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
}

const ListView = ({
  brands,
  isLoading,
  error,
  onEdit,
  onDelete,
  onCreate,
  onClose,
}) => (
  <>
    <DialogTitle sx={{ pr: 6 }}>
      {i18n.t("trademarkSettings.title")}
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: theme => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
    </DialogTitle>

    <DialogContent>
      <Typography sx={{ fontSize: "14px", color: "#6d6d6d", mb: 3 }}>
        {i18n.t("trademarkSettings.modalDescription")}
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button
          variant="contained"
          color="branded"
          size="small"
          startIcon={<AddIcon />}
          onClick={onCreate}
        >
          {i18n.t("trademarkSettings.newBrand")}
        </Button>
      </Box>
      {isLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "120px",
          }}
        >
          <CircularProgress />
        </Box>
      )}
      {error && (
        <Typography sx={{ fontSize: "14px", color: "#e53935" }}>
          {i18n.t("trademarkSettings.errorLoading")}
        </Typography>
      )}
      {!isLoading && !error && brands.length === 0 && (
        <Typography sx={{ fontSize: "14px", color: "#6d6d6d" }}>
          {i18n.t("trademarkSettings.noBrandsYet")}
        </Typography>
      )}
      {!isLoading &&
        !error &&
        brands.map(brand => (
          <BrandCard
            key={brand.id}
            brand={brand}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
    </DialogContent>

    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onClose} color="branded" variant="outlined">
        {i18n.t("trademarkSettings.close")}
      </Button>
    </DialogActions>
  </>
)

ListView.propTypes = {
  brands: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  error: PropTypes.object,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
}

const EditView = ({ brand, isNew, onBack, onClose, onSave, isSaving }) => {
  const [tab, setTab] = useState(0)
  const wasDefault = brand?.isDefault || false
  const [generalInfo, setGeneralInfo] = useState({
    name: brand?.name || "",
    isDefault: wasDefault,
  })
  const [brandingInfo, setBrandingInfo] = useState({
    logo: brand?.logo || "",
    colorPrimary: brand?.colorPrimary || (isNew ? DEFAULT_TEXT_COLOR : ""),
    colorSecondary:
      brand?.colorSecondary || (isNew ? DEFAULT_BACKGROUND_COLOR : ""),
  })
  const [pdfInfo, setPdfInfo] = useState({
    cover: brand?.cover || "",
    signature: brand?.signature || "",
    conditions: brand?.conditions || "",
  })
  return (
    <>
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", gap: 1, pr: 6 }}
      >
        <IconButton
          size="small"
          onClick={onBack}
          aria-label={i18n.t("trademarkSettings.backToList")}
          sx={{ mr: 0.5 }}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        {isNew
          ? i18n.t("trademarkSettings.createBrand")
          : i18n.t("trademarkSettings.editBrand")}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          aria-label="tabs editar marca"
          sx={{ mb: 2 }}
        >
          <Tab label={i18n.t("trademarkSettings.tabGeneral")} />
          <Tab label={i18n.t("trademarkSettings.tabPdf")} />
        </Tabs>

        {tab === 0 && (
          <EditBrandGeneralTab
            generalInfo={generalInfo}
            setGeneralInfo={setGeneralInfo}
            brandingInfo={brandingInfo}
            setBrandingInfo={setBrandingInfo}
            canSetDefault={!isNew && !wasDefault}
          />
        )}
        {tab === 1 && (
          <EditBrandPdfTab pdfInfo={pdfInfo} setPdfInfo={setPdfInfo} />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onBack}
          color="branded"
          variant="outlined"
          disabled={isSaving}
        >
          {i18n.t("trademarkSettings.cancel")}
        </Button>
        <Button
          onClick={() =>
            onSave(
              {
                name: generalInfo.name,
                logo: brandingInfo.logo,
                colorPrimary: brandingInfo.colorPrimary,
                colorSecondary: brandingInfo.colorSecondary,
                cover: pdfInfo.cover,
                signature: pdfInfo.signature,
                conditions: pdfInfo.conditions,
              },
              { promoteToDefault: generalInfo.isDefault && !wasDefault },
            )
          }
          color="branded"
          variant="contained"
          disabled={isSaving || !generalInfo.name.trim()}
        >
          {isNew
            ? i18n.t("trademarkSettings.create")
            : i18n.t("trademarkSettings.save")}
        </Button>
      </DialogActions>
    </>
  )
}

EditView.propTypes = {
  brand: PropTypes.shape({
    name: PropTypes.string,
    isDefault: PropTypes.bool,
    logo: PropTypes.string,
    colorPrimary: PropTypes.string,
    colorSecondary: PropTypes.string,
    cover: PropTypes.string,
    signature: PropTypes.string,
    conditions: PropTypes.string,
  }),
  onBack: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isNew: PropTypes.bool,
  isSaving: PropTypes.bool,
}

const DeleteConfirmView = ({
  brand,
  onBack,
  onClose,
  onConfirm,
  isDeleting,
}) => (
  <>
    <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 6 }}>
      <IconButton
        size="small"
        onClick={onBack}
        aria-label={i18n.t("trademarkSettings.backToList")}
        sx={{ mr: 0.5 }}
      >
        <ArrowBackIcon fontSize="small" />
      </IconButton>
      {i18n.t("trademarkSettings.deleteTitle")}
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: theme => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent>
      <Typography sx={{ fontSize: "14px", color: "#6d6d6d" }}>
        {i18n.t("trademarkSettings.deleteConfirm")}
        {brand?.name && (
          <Box component="span" sx={{ fontWeight: 600, color: "#414141" }}>
            {" "}
            {brand.name}
          </Box>
        )}
        {"?"}
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button
        onClick={onBack}
        color="branded"
        variant="outlined"
        disabled={isDeleting}
      >
        {i18n.t("trademarkSettings.cancel")}
      </Button>
      <Button
        onClick={onConfirm}
        variant="contained"
        disabled={isDeleting}
        sx={{
          backgroundColor: "#e53935",
          "&:hover": { backgroundColor: "#c62828" },
        }}
      >
        {i18n.t("trademarkSettings.delete")}
      </Button>
    </DialogActions>
  </>
)

DeleteConfirmView.propTypes = {
  brand: PropTypes.shape({ name: PropTypes.string }),
  onBack: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  isDeleting: PropTypes.bool,
}

const ModalTrademarks = ({ open, onClose }) => {
  const { user } = useAuthContext()
  const organizationId = user?.["custom:organizationId"]
  const { enqueueSnackbar } = useSnackbar()
  const [view, setView] = useState("list")
  const [activeBrand, setActiveBrand] = useState(null)

  const {
    data: brands,
    isLoading,
    error,
  } = useBrands(organizationId, { enabled: open })

  const updateBrand = useUpdateBrand(organizationId)
  const createBrand = useCreateBrand(organizationId)
  const deleteBrand = useDeleteBrand(organizationId)
  const setDefaultBrand = useSetDefaultBrand(organizationId)

  const handleClose = () => {
    setView("list")
    setActiveBrand(null)
    onClose()
  }

  const handleEdit = brand => {
    setActiveBrand(brand)
    setView("edit")
  }

  const handleCreate = () => {
    setActiveBrand(null)
    setView("create")
  }

  const handleDelete = brand => {
    setActiveBrand(brand)
    setView("delete")
  }

  const handleBack = () => {
    setView("list")
    setActiveBrand(null)
  }

  const handleSave = (changes, { promoteToDefault } = {}) => {
    const promote = () =>
      promoteToDefault
        ? setDefaultBrand.mutateAsync(activeBrand.id)
        : Promise.resolve()

    updateBrand.mutate(
      { brandId: activeBrand.id, brand: changes },
      {
        onSuccess: async () => {
          try {
            await promote()
          } catch {
            enqueueSnackbar(i18n.t("trademarkSettings.defaultSetError"), {
              variant: "error",
            })
            return
          }
          enqueueSnackbar(i18n.t("trademarkSettings.savedSuccess"), {
            variant: "success",
          })
          handleBack()
        },
        onError: () => {
          enqueueSnackbar(i18n.t("trademarkSettings.saveError"), {
            variant: "error",
          })
        },
      },
    )
  }

  const handleConfirmCreate = values => {
    createBrand.mutate(values, {
      onSuccess: () => {
        enqueueSnackbar(i18n.t("trademarkSettings.createdSuccess"), {
          variant: "success",
        })
        handleBack()
      },
      onError: () => {
        enqueueSnackbar(i18n.t("trademarkSettings.createError"), {
          variant: "error",
        })
      },
    })
  }

  const handleConfirmDelete = () => {
    deleteBrand.mutate(activeBrand.id, {
      onSuccess: () => {
        enqueueSnackbar(i18n.t("trademarkSettings.deletedSuccess"), {
          variant: "success",
        })
        handleBack()
      },
      onError: () => {
        enqueueSnackbar(i18n.t("trademarkSettings.deleteError"), {
          variant: "error",
        })
      },
    })
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      {view === "list" && (
        <ListView
          brands={brands || []}
          isLoading={isLoading}
          error={error}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onCreate={handleCreate}
          onClose={handleClose}
        />
      )}
      {view === "create" && (
        <EditView
          isNew
          onBack={handleBack}
          onClose={handleClose}
          onSave={handleConfirmCreate}
          isSaving={createBrand.isLoading}
        />
      )}
      {view === "edit" && (
        <EditView
          key={activeBrand?.id}
          brand={activeBrand}
          onBack={handleBack}
          onClose={handleClose}
          onSave={handleSave}
          isSaving={updateBrand.isLoading}
        />
      )}
      {view === "delete" && (
        <DeleteConfirmView
          brand={activeBrand}
          onBack={handleBack}
          onClose={handleClose}
          onConfirm={handleConfirmDelete}
          isDeleting={deleteBrand.isLoading}
        />
      )}
    </Dialog>
  )
}

ModalTrademarks.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default ModalTrademarks
